import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { useLang } from "../../context/LanguageContext";
import { WordTitle, UpTitle } from "./WordTitle";
import { IMAGES } from "../../constants/images";

const PAIRS = [
  [IMAGES.hero_studio, IMAGES.bike_detail],
  [IMAGES.class_energy, IMAGES.gallery_shoes],
  [IMAGES.sprint_energy, IMAGES.rider_silhouette],
];

const PhasePair = ({ progress, i, pair }) => {
  const clip = useTransform(
    progress,
    [Math.max(0, i * 0.33 - 0.1), i * 0.33 + 0.08],
    [i === 0 ? "inset(0% 0px 0% 0px)" : "inset(100% 0px 0% 0px)", "inset(0% 0px 0% 0px)"]
  );
  const y1 = useTransform(progress, [0, 1], [30, -30]);
  const y2 = useTransform(progress, [0, 1], [-24, 24]);
  return (
    <motion.div
      style={{ clipPath: clip }}
      className="absolute inset-0 bg-[#050505] flex items-center justify-center gap-5 md:gap-10 px-6 md:px-14"
    >
      <div className="w-[82%] md:w-[54%] h-[52vh] md:h-[68vh] overflow-hidden">
        <motion.img src={pair[0]} alt={`Apex phase ${i + 1}`} style={{ y: y1, scale: 1.1 }} className="w-full h-full object-cover" />
      </div>
      <div className="hidden md:block w-[22%] h-[46vh] overflow-hidden self-end mb-[10vh]">
        <motion.img src={pair[1]} alt="" style={{ y: y2, scale: 1.12 }} className="w-full h-full object-cover" />
      </div>
    </motion.div>
  );
};

const Txt = ({ text }) =>
  text.split("*").map((seg, i) =>
    i % 2 === 1 ? (
      <em key={i} className="font-serif italic font-medium text-[#C5A059]">{seg}</em>
    ) : (
      <span key={i}>{seg}</span>
    )
  );

export const ScrollerStepper = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setActive(Math.min(2, Math.floor(v * 3.2)));
  });

  return (
    <section id="experience" data-testid="stepper-section">
      <div className="px-6 md:px-14 py-24 md:py-36 text-center">
        <UpTitle text={t.stepper.uptitle} />
        <h2 className="mt-10 font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.05] text-white">
          <WordTitle lines={t.stepper.title} />
        </h2>
      </div>

      <div ref={ref} className="relative h-[350vh]">
        <div className="sticky top-0 h-screen overflow-hidden">
          {PAIRS.map((pair, i) => (
            <PhasePair key={i} progress={scrollYProgress} i={i} pair={pair} />
          ))}

          <div className="absolute left-6 md:left-14 bottom-10 md:bottom-14 z-10">
            {t.stepper.steps.map((s, i) => (
              <div key={i} className="overflow-hidden">
                <div
                  className="font-serif text-4xl md:text-6xl lg:text-7xl leading-tight text-white drop-shadow-[0_2px_16px_rgba(0,0,0,0.7)]"
                  style={{
                    transform: active >= i ? "translateY(0%)" : "translateY(115%)",
                    transition: "transform 0.8s cubic-bezier(0.22,1,0.36,1)",
                    transitionDelay: `${i * 0.06}s`,
                  }}
                >
                  <Txt text={s} />
                </div>
              </div>
            ))}
          </div>

          <div className="absolute right-6 md:right-14 top-1/2 -translate-y-1/2 z-10 flex flex-col gap-4" data-testid="stepper-bullets">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="w-2.5 h-2.5 rounded-full transition-all duration-500"
                style={{
                  background: active === i ? "#C5A059" : "rgba(255,255,255,0.3)",
                  transform: active === i ? "scale(1.4)" : "scale(1)",
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
